import appConfig from "@config/app";
import { ASK_SW_BUILD_HASH, mtLogger } from "shared-core";

const makePageControllerPromise = (): Promise<ServiceWorker> =>
  new Promise(resolve => {
    if (navigator.serviceWorker.controller) {
      resolve(navigator.serviceWorker.controller);
    }
    navigator.serviceWorker.addEventListener("controllerchange", _ => {
      if (navigator.serviceWorker.controller) {
        resolve(navigator.serviceWorker.controller);
      }
    });
  });

// A stale (cached) service worker still runs the old code and replies with a
// plain hash string, so we accept both that and the newer { hash, time } shape.
type SWBuildInfo = string | { hash: string; time: string | null };

const followSWBuildHash = (): void => {
  makePageControllerPromise()
    .then((controller: ServiceWorker): Promise<SWBuildInfo> => {
      const msgChannel = new MessageChannel();
      const rv = new Promise<SWBuildInfo>(r => {
        msgChannel.port1.onmessage = (e): void => r(e.data);
      });
      controller.postMessage({ type: ASK_SW_BUILD_HASH }, [msgChannel.port2]);
      return rv;
    })
    .then((swBuild: SWBuildInfo) => {
      const swBuildHash = typeof swBuild === "string" ? swBuild : swBuild?.hash;
      const swBuildTime = typeof swBuild === "string" ? null : swBuild?.time;
      mtLogger.debug(`ServiceWorker controlling page is ${swBuildHash}`);
      if (swBuildHash) {
        appConfig["SW_BUILD_HASH"] = swBuildHash;
        appConfig["SW_BUILD_TIME"] = swBuildTime ?? null;
      }
    });
};

// Check if service worker are supported
export const useFollowServiceWorker = (): void => {
  // Contrived mechanism to detect and update which service worker is
  // controlling the page. It can change during an update (because we use
  // `skipWaiting`).
  followSWBuildHash();
  navigator.serviceWorker &&
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      mtLogger.debug(`ServiceWorker controller is changing`);
      followSWBuildHash();
    });
};
